import { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { AppShell } from "@/components/AppShell"
import { GoogleButton } from "@/components/GoogleButton"
import { Badge } from "@/components/ui/badge"
import { PageHeader } from "@/components/PageHeader"
import { FormRow, FormSection } from "@/components/blocks/FormRows"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ProfileSubNav } from "./ProfileSubNav"

import type { PageProps } from "@/types/inertia"

type GoogleStatus = { enabled: boolean; connected: boolean; email: string | null }

export default function ProfileDetails() {
  const intl = useIntl()
  const { props } = usePage<PageProps<{ google: GoogleStatus }>>()
  const google = props.google
  const user = props.current_user
  const errors = props.errors ?? {}

  const form = useForm({ name: user?.name ?? "", email: user?.email ?? "" })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.patch("/profile", { preserveScroll: true })
  }

  return (
    <>
      <Head title={intl.formatMessage({ id: "profile.tab_details" })}>
        <meta name="description" content={intl.formatMessage({ id: "profile.details_meta" })} />
        <meta property="og:title" content={intl.formatMessage({ id: "profile.tab_details" })} />
        <meta property="og:description" content={intl.formatMessage({ id: "profile.details_meta" })} />
      </Head>
      <AppShell>
        <PageHeader
          title={intl.formatMessage({ id: "profile.title" })}
          description={intl.formatMessage({ id: "profile.subtitle" })}
          tabs={<ProfileSubNav active="details" />}
        />

        <form onSubmit={submit} className="mt-10 max-w-3xl">
          <FormSection title={intl.formatMessage({ id: "profile.details_title" })} description={intl.formatMessage({ id: "profile.details_description" })}>
            <FormRow
              label={intl.formatMessage({ id: "profile.name_label" })}
              htmlFor="name"
              hint={intl.formatMessage({ id: "profile.name_hint" })}
              hintId="name_help"
            >
              <Input
                id="name"
                autoComplete="name"
                maxLength={100}
                aria-invalid={!!errors.name}
                aria-describedby="name_help"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                className="sm:max-w-md"
              />
              {errors.name && <p className="mt-2 text-xs text-danger-display">{errors.name}</p>}
            </FormRow>
            <FormRow label={intl.formatMessage({ id: "profile.email_label" })} htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                aria-invalid={!!errors.email}
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
                className="sm:max-w-md"
              />
              {errors.email && <p className="mt-2 text-xs text-danger-display">{errors.email}</p>}
            </FormRow>
          </FormSection>
          <div className="mt-6 flex justify-end">
            <Button type="submit" disabled={form.processing}>
              {intl.formatMessage({ id: "profile.save" })}
            </Button>
          </div>
        </form>

        {google.enabled && (
          <div className="mt-12 max-w-3xl">
            <FormSection title={intl.formatMessage({ id: "profile.google_title" })} description={intl.formatMessage({ id: "profile.google_description" })}>
              <FormRow label={intl.formatMessage({ id: "profile.google_status" })}>
                {google.connected ? (
                  <p className="flex flex-wrap items-center gap-2">
                    <Badge tone="accent">{intl.formatMessage({ id: "profile.google_connected" })}</Badge>
                    <span>{google.email ? intl.formatMessage({ id: "profile.google_connected_email" }, { email: google.email }) : intl.formatMessage({ id: "profile.google_connected_body" })}</span>
                  </p>
                ) : (
                  <div className="space-y-4">
                    <p className="flex flex-wrap items-center gap-2">
                      <Badge tone="muted">{intl.formatMessage({ id: "profile.google_not_connected" })}</Badge>
                      <span>{intl.formatMessage({ id: "profile.google_not_connected_body" })}</span>
                    </p>
                    <div className="max-w-xs">
                      <GoogleButton label={intl.formatMessage({ id: "profile.google_connect" })} />
                    </div>
                  </div>
                )}
              </FormRow>
            </FormSection>
          </div>
        )}
      </AppShell>
    </>
  )
}
